import { useDispatch, useSelector } from 'react-redux';
import { ShadowStyle } from 'consts/shadow';
import { changeDealData } from 'store/dealManagement/reducer';
import { Label } from 'lib/Label';
import { Padding } from 'lib/BaseSelect/BaseSelect.types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import AsyncSelect from 'lib/AsyncSelect';
import { SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { isReadOnly } from 'utils/isReadOnly';
import { getIsReseller } from 'store/publisher/selectors';
import { getSpecialist } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { isRequiredDirectSalesField } from 'utils/isRequiredDirectSalesField';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectSalesSpecialistProps {
  isDisabled?: boolean;
}

const SelectSpecialist = ({ isDisabled }: SelectSalesSpecialistProps): React.ReactElement | null => {
  const dispatch = useDispatch();

  const specialist = useSelector(getSpecialist);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const directSalesRequiredFieldsConfig = useSelector((state: Store) => state.publisher.directSalesRequiredFields);
  const isRequired = isRequiredDirectSalesField({
    fieldsConfig: directSalesRequiredFieldsConfig,
    fieldName: 'specialist',
  });
  const isReseller = useSelector(getIsReseller);

  const { onLoadLookup } = useLoadLookup();

  const { isCampaignReadOnly } = useIsReadOnly();
  const { getPendoDataId } = usePendoTracking();

  const handleSelectSpecialist: SelectHandler<CodeNameModel> = (dropdownName, value): void => {
    dispatch(changeDealData({ [dropdownName]: value }));
  };

  if (isReseller) return null;

  return (
    <>
      <Label id="specialist" theme={InputTheme.FLAT_GRAY} label="Specialist" isRequired={isRequired} />
      <Tooltip tooltip={specialist?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <AsyncSelect
          name="specialist"
          dataTestId="async-select-specialist"
          placeholder=""
          theme={InputTheme.WHITE_WITH_BORDER}
          size={InputSize.EXTRA_SMALL}
          padding={Padding.NONE}
          shadow={ShadowStyle.NONE}
          shape={InputShape.ROUNDED}
          selectedValue={specialist}
          loadData={onLoadLookup}
          onSelect={handleSelectSpecialist}
          isDisabled={isDisabled || isReadOnly(bookingStatusCode, isEditingDisabled) || isCampaignReadOnly}
          dataPendoId={getPendoDataId('Specialist')}
        />
      </Tooltip>
    </>
  );
};

export default SelectSpecialist;
